import * as api from './api';

export type QueuedActionType =
	| 'entrata'
	| 'uscita'
	| 'riapriGiornata'
	| 'entraCantiere'
	| 'esciCantiere'
	| 'entraSessioneProvvisoria'
	| 'esciSessioneProvvisoria';
export interface QueuedAction {
	id: string;
	type: QueuedActionType;
	ownerId?: string;
	payload: {
		device_timestamp: string;
		latitudine?: number;
		longitudine?: number;
		accuratezza_gps_m?: number;
		gps_position_timestamp?: string;
		cantiere_id?: string;
		sessione_id?: string;
		nota_lavoro?: string;
		pausa_minuti?: number;
	};
	queuedAt: string;
	sequence?: number;
	lastError?: string;
	lastAttemptAt?: string;
}

const STORAGE_KEY = 'offline_action_queue';
const ENTRY_PREFIX = STORAGE_KEY + ':';
const ARCHIVE_PREFIX = 'offline_action_archive:';
const CHANGE_EVENT = 'offline-queue-change';
const TYPES: QueuedActionType[] = [
	'entrata',
	'uscita',
	'riapriGiornata',
	'entraCantiere',
	'esciCantiere',
	'entraSessioneProvvisoria',
	'esciSessioneProvvisoria',
];
export class QueueStorageError extends Error {
	constructor() {
		super(
			'Memoria del dispositivo non disponibile o coda non leggibile. Operazione non completata: non creare altre timbrature e non cancellare i dati dell’app. Riprova dal riepilogo o contatta il responsabile.',
		);
	}
}

// Never prune by age or replace unreadable storage with an empty queue.
export function getQueue(): QueuedAction[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		const items: unknown = raw ? JSON.parse(raw) : [];
		if (!Array.isArray(items)) throw new Error();
		for (let index = 0; index < localStorage.length; index++) {
			const key = localStorage.key(index);
			if (key?.startsWith(ENTRY_PREFIX)) items.push(JSON.parse(localStorage.getItem(key)!));
		}
		if (
			!Array.isArray(items) ||
			items.some(
				(a) =>
					!a ||
					typeof a.id !== 'string' ||
					!TYPES.includes(a.type) ||
					!a.payload ||
					typeof a.payload.device_timestamp !== 'string' ||
					typeof a.queuedAt !== 'string',
			)
		)
			throw new Error();
		if (new Set(items.map((a) => a.id)).size !== items.length) throw new Error();
		const archivedIds = new Set(
			getArchives().flatMap((group) => group.actions.map((action) => action.id)),
		);
		return items
			.filter((action) => !archivedIds.has(action.id))
			.sort(
				(a, b) =>
					(a.sequence ?? 0) - (b.sequence ?? 0) ||
					a.queuedAt.localeCompare(b.queuedAt) ||
					a.id.localeCompare(b.id),
			);
	} catch {
		throw new QueueStorageError();
	}
}

// Separate entries avoid overwriting another tab's concurrent append/acknowledgement.
function saveAction(action: QueuedAction) {
	try {
		const legacy: QueuedAction[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
		if (legacy.some((item) => item.id === action.id)) {
			localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify(legacy.map((item) => (item.id === action.id ? action : item))),
			);
		} else localStorage.setItem(ENTRY_PREFIX + action.id, JSON.stringify(action));
	} catch {
		throw new QueueStorageError();
	}
	window.dispatchEvent(new Event(CHANGE_EVENT));
}

function removeAction(id: string) {
	try {
		if (localStorage.getItem(ENTRY_PREFIX + id) !== null)
			localStorage.removeItem(ENTRY_PREFIX + id);
		else {
			const legacy: QueuedAction[] = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
			localStorage.setItem(
				STORAGE_KEY,
				JSON.stringify(legacy.filter((action) => action.id !== id)),
			);
		}
	} catch {
		throw new QueueStorageError();
	}
	window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function subscribe(listener: () => void) {
	const onStorage = (event: StorageEvent) => {
		if (
			event.key === STORAGE_KEY ||
			event.key?.startsWith(ENTRY_PREFIX) ||
			event.key?.startsWith(ARCHIVE_PREFIX) ||
			event.key === null
		)
			listener();
	};
	window.addEventListener(CHANGE_EVENT, listener);
	window.addEventListener('storage', onStorage);
	return () => {
		window.removeEventListener(CHANGE_EVENT, listener);
		window.removeEventListener('storage', onStorage);
	};
}

export function enqueue(type: QueuedActionType, payload: QueuedAction['payload']): QueuedAction {
	const ownerId = api.getQueueOwnerId();
	if (!ownerId)
		throw new Error('Impossibile identificare il dipendente. Accedi nuovamente prima di timbrare.');
	const queue = getQueue();
	const item: QueuedAction = {
		id: crypto.randomUUID(),
		type,
		ownerId,
		payload: { ...payload },
		queuedAt: new Date().toISOString(),
		sequence: Math.max(0, ...queue.map((action) => action.sequence ?? 0)) + 1,
	};
	saveAction(item); // Durable before the first attempt, including online actions.
	return item;
}

export interface SyncResult {
	synced: number;
	remaining: number;
	errors: string[];
	confirmedIds: string[];
	confirmedActions: QueuedAction[];
	warnings: string[];
	blocked?: 'identity' | 'rejected' | 'unconfirmed';
}
let inFlight: Promise<SyncResult> | undefined;

/** One ordered worker. A rejected or ambiguous request blocks all dependent actions. */
export function processQueue(): Promise<SyncResult> {
	if (inFlight) return inFlight;
	const run = () => runQueue();
	const work = navigator.locks ? navigator.locks.request('timbrature-offline-sync', run) : run();
	inFlight = work.finally(() => {
		inFlight = undefined;
	});
	return inFlight;
}

async function runQueue(): Promise<SyncResult> {
	const result: SyncResult = {
		synced: 0,
		remaining: 0,
		errors: [],
		confirmedIds: [],
		confirmedActions: [],
		warnings: [],
	};
	// Insertion order survives clock corrections and preserves user intent.
	const queue = getQueue();
	for (const action of queue) {
		const ownerId = api.getQueueOwnerId();
		if (!ownerId || !action.ownerId || action.ownerId !== ownerId) {
			result.blocked = 'identity';
			result.errors.push(
				!action.ownerId
					? 'Una timbratura precedente non ha un dipendente verificabile. Mostra il riepilogo al responsabile prima di procedere.'
					: 'Ci sono timbrature di un altro accesso. Rientra con il dipendente che le ha salvate.',
			);
			break;
		}
		if (!navigator.onLine) break;
		try {
			// Save an attempt marker before sending; a storage failure must stop transmission.
			const current = getQueue();
			const stored = current.find((a) => a.id === action.id);
			if (!stored) continue;
			stored.lastAttemptAt = new Date().toISOString();
			saveAction(stored);
			let response: { data: unknown; warning?: string };
			switch (action.type) {
				case 'entrata':
					response = await api.entrata(action.payload, action.id, ownerId);
					break;
				case 'uscita':
					response = await api.uscita(action.payload, action.id, ownerId);
					break;
				case 'riapriGiornata':
					response = await api.riapriGiornata(action.payload, action.id, ownerId);
					break;
				case 'entraCantiere':
					response = await api.entraCantiere(
						{ ...action.payload, cantiere_id: action.payload.cantiere_id! },
						action.id,
						ownerId,
					);
					break;
				case 'esciCantiere':
					response = await api.esciCantiere(
						{ ...action.payload, cantiere_id: action.payload.cantiere_id! },
						action.id,
						ownerId,
					);
					break;
				case 'entraSessioneProvvisoria':
					response = await api.entraSessioneProvvisoria(action.payload, action.id, ownerId);
					break;
				case 'esciSessioneProvvisoria':
					response = await api.esciSessioneProvvisoria(action.payload, action.id, ownerId);
					break;
			}
			if (!response || !Object.prototype.hasOwnProperty.call(response, 'data')) {
				throw new Error(
					'Risposta del server incompleta: esito da verificare. Riprova dal riepilogo senza creare un’altra timbratura.',
				);
			}
			removeAction(action.id);
			result.confirmedIds.push(action.id);
			result.confirmedActions.push(action);
			result.synced++;
			if (response.warning) result.warnings.push(response.warning);
		} catch (error) {
			if (error instanceof QueueStorageError) {
				if (!result.synced) throw error;
				result.errors.push(error.message);
				break;
			}
			const message =
				error instanceof Error ? error.message : 'Esito non verificato. Riprova dal riepilogo.';
			result.blocked =
				error instanceof api.ApiError && error.status >= 400 && error.status < 500
					? 'rejected'
					: 'unconfirmed';
			const current = getQueue();
			const stored = current.find((a) => a.id === action.id);
			if (stored) {
				stored.lastError = message;
				saveAction(stored);
			}
			result.errors.push(message);
			break;
		}
	}
	try {
		result.remaining = getQueue().length;
	} catch (error) {
		result.remaining = queue.length - result.synced;
		result.errors.push(error instanceof Error ? error.message : 'Coda non leggibile');
	}
	return result;
}

export interface ArchivedGroup {
	id: string;
	archivedAt: string;
	reason: string;
	actions: QueuedAction[];
}

export function getArchives(): ArchivedGroup[] {
	try {
		const groups: ArchivedGroup[] = [];
		for (let index = 0; index < localStorage.length; index++) {
			const key = localStorage.key(index);
			if (!key?.startsWith(ARCHIVE_PREFIX)) continue;
			const group = JSON.parse(localStorage.getItem(key)!);
			if (
				!group ||
				!Array.isArray(group.actions) ||
				!group.actions.every((action: QueuedAction) => typeof action?.id === 'string')
			)
				throw new Error();
			groups.push(group);
		}
		return groups;
	} catch {
		throw new QueueStorageError();
	}
}

/** Explicit user resolution only. One atomic write archives the whole dependent group.
 * Original entries remain intact as well; nothing is sent or counted as synchronized. */
export async function archiveQueue(): Promise<void> {
	const archive = async () => {
		const actions = getQueue();
		if (!actions.length) return;
		const ownerId = api.getQueueOwnerId();
		if (!ownerId || actions.some((action) => action.ownerId && action.ownerId !== ownerId)) {
			throw new Error('Accedi con il dipendente proprietario prima di gestire questa coda.');
		}
		const group: ArchivedGroup = {
			id: crypto.randomUUID(),
			archivedAt: new Date().toISOString(),
			reason:
				'Verificata con il responsabile; archiviata esplicitamente sul dispositivo, non sincronizzata.',
			actions,
		};
		try {
			localStorage.setItem(ARCHIVE_PREFIX + group.id, JSON.stringify(group));
		} catch {
			throw new QueueStorageError();
		}
		window.dispatchEvent(new Event(CHANGE_EVENT));
	};
	if (navigator.locks) await navigator.locks.request('timbrature-offline-sync', archive);
	else {
		if (inFlight) await inFlight;
		await archive();
	}
}

export function readState(): {
	actions: QueuedAction[];
	archives: ArchivedGroup[];
	error: string | null;
} {
	try {
		return { actions: getQueue(), archives: getArchives(), error: null };
	} catch (error) {
		return {
			actions: [],
			archives: [],
			error: error instanceof Error ? error.message : 'Coda non leggibile',
		};
	}
}
